import { useRef } from "react";
import DocIcon from "../../../../assets/icons/doc.svg";
import ViewIcon from "../../../../assets/icons/visibility.svg";
import {
  FlaggableWrap,
  getFlagKey,
  isUpdatedReviewField,
  toDocumentFlagField,
} from "../ReviewField";
import { useHrDirectEditContext } from "../../HrDirectEditContext";
import { HR_DOCUMENT_ACCEPT } from "../../../../utils/hrDirectEdit";
import { listedHrEditFieldKey } from "../../../../utils/onboardingHelpers";
import { HrFieldApiHistoryPanel } from "../HrEditHistory";

const SectionDocuments = ({
  sectionKey,
  documents = [],
  getFileIcon,
  onPreviewDoc,
  fieldFlags = {},
  flagModeEnabled = false,
  restrictToUpdatedFields = false,
  updatedFields = [],
  flagModal = null,
  focusedFlagKey = null,
  onOpenFlagModal,
}) => {
  const hrEdit = useHrDirectEditContext();
  const fileInputsRef = useRef({});

  return (
    <div className="rr-documents">
      <div className="flex items-center resume-head">
        <img className="nav-icon" src={DocIcon} alt="documents-icon" />
        <div className="form-subhead">documents</div>
      </div>

      {documents.length > 0 ? (
        <div
          className={`rr-documents-list${
            restrictToUpdatedFields ? " rr-documents-list--updated-filter" : ""
          }`}
        >
          {documents.map((doc) => {
            const field = toDocumentFlagField(doc);
            const flagKey = getFlagKey(sectionKey, field.key);
            const isUpdatedField = isUpdatedReviewField(
              updatedFields,
              sectionKey,
              field.key,
            );
            const isFilterDisabled = restrictToUpdatedFields && !isUpdatedField;
            const showUpdatedHighlight =
              restrictToUpdatedFields && isUpdatedField;
            const canFlag =
              !!flagModeEnabled &&
              !!sectionKey &&
              !!field.key &&
              (!restrictToUpdatedFields || isUpdatedField);
            const fieldFlag = fieldFlags[flagKey];
            const isFlagged = !!fieldFlag;
            const isModalOpenForField =
              canFlag && flagModal?.flagKey === flagKey;
            const canReplace = !!hrEdit?.isDocumentReplaceable?.(
              sectionKey,
              doc.id,
            );
            const replaceSession = hrEdit?.session;
            const isReplacing =
              replaceSession?.kind === "document" &&
              replaceSession.sectionKey === sectionKey &&
              replaceSession.docId === doc.id;
            const listedHistoryKey = listedHrEditFieldKey(
              hrEdit?.hrEditedFields,
              sectionKey,
              doc.id,
              null,
            );
            const historyFieldKey = listedHistoryKey || doc.id;
            const historyOpen = !!hrEdit?.isHistoryOpen?.(
              sectionKey,
              historyFieldKey,
              null,
            );
            const apiHistoryState = historyOpen
              ? hrEdit?.getHistoryState?.(sectionKey, historyFieldKey, null)
              : null;

            return (
              <div key={doc.id} className="rr-document-flag-wrap">
                <FlaggableWrap
                  flagKey={flagKey}
                  canFlag={canFlag && !isReplacing}
                  isFlagged={isFlagged}
                  isModalOpenForField={isModalOpenForField}
                  focusedFlagKey={focusedFlagKey}
                  isFilterDisabled={isFilterDisabled}
                  showUpdatedHighlight={showUpdatedHighlight}
                  fieldFlag={fieldFlag}
                  showFlaggedIcon={false}
                  onOpenFlagModal={(event) =>
                    onOpenFlagModal?.(sectionKey, field, event, {
                      isDocument: true,
                    })
                  }
                >
                  <div className="rr-document-card">
                    <img
                      className="rr-document-icon"
                      src={getFileIcon(doc.type)}
                      alt=""
                    />
                    <div className="rr-document-meta">
                      <div className="rr-document-name-row">
                        <div className="rr-document-name">{doc.name}</div>
                      </div>
                      {doc.size && (
                        <div className="rr-document-size">{doc.size}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="rr-document-view-btn"
                      title={`View ${doc.name}`}
                      aria-label={`View ${doc.name}`}
                      onClick={() => onPreviewDoc(doc)}
                    >
                      <img src={ViewIcon} alt="" />
                    </button>
                    {hrEdit?.enabled && !canReplace && !isFilterDisabled ? (
                      <span
                        className="material-symbols-rounded rr-doc-lock-icon"
                        title="Locked"
                        aria-label="Locked"
                      >
                        lock
                      </span>
                    ) : null}
                    {canReplace && !isFilterDisabled ? (
                      <>
                        <input
                          ref={(node) => {
                            fileInputsRef.current[doc.id] = node;
                          }}
                          type="file"
                          accept={HR_DOCUMENT_ACCEPT}
                          hidden
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (file) {
                              hrEdit.replaceDocumentFile({
                                sectionKey,
                                doc,
                                file,
                              });
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="rr-hr-replace-btn"
                          onClick={() => fileInputsRef.current[doc.id]?.click()}
                        >
                          Replace
                        </button>
                      </>
                    ) : null}
                  </div>
                </FlaggableWrap>
                {historyOpen && !isReplacing ? (
                  <HrFieldApiHistoryPanel state={apiHistoryState} />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rr-documents-empty">
          No documents attached for this section.
        </div>
      )}
    </div>
  );
};

export default SectionDocuments;
